/**
 * Bottom navigation, phones only.
 *
 * The top bar does not fit a phone. Measured at 375 px before this existed:
 * the document was 625 px wide, the bar ran off the right edge, "Personnel" and
 * "Mon compte" were unreachable, and every page scrolled sideways. This app is
 * an installed PWA — a phone is the common case, not the edge case.
 *
 * Tiles are derived from PERMISSIONS, not from a list of roles. The shipped
 * SPA hardcoded `roles: ["director", "gestionnaire", ...]` on each tab, which
 * drifts the moment a role is added — the Founder role would have needed an
 * edit in nine places. Here a route declares `nav.mobile` (its tile order) and
 * the existing permission gate decides who sees it.
 *
 * Five tiles maximum: past that the labels truncate to nonsense at 375 px. The
 * bar is hidden entirely below two tiles, because a "navigation" offering one
 * destination is furniture.
 */
import { NavLink } from "react-router-dom";
import type { AppRoute } from "./routes";

/** Max tiles that stay legible at 375 px. */
const MAX_TILES = 5;

/**
 * The tiles this bar will show, in order.
 *
 * Exported so the shell can compute the OVERFLOW as `items - bottomTiles` and
 * put it in the account menu. Deriving both from one function is what keeps a
 * destination from falling between the two: every permitted route is either a
 * tile or in the menu.
 */
export function bottomTiles(items: AppRoute[]): AppRoute[] {
  const tiles = items
    .filter((route) => route.nav?.mobile !== undefined)
    .sort((a, b) => a.nav!.mobile! - b.nav!.mobile!)
    .slice(0, MAX_TILES);
  // A "navigation" offering one destination is furniture, not navigation.
  return tiles.length < 2 ? [] : tiles;
}

const ICONS: Record<string, string> = {
  home: "M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z",
  students: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 9a8 8 0 0 1 16 0Z",
  school: "M12 3 2 8l10 5 10-5-10-5Zm-6 8v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5l-6 3-6-3Z",
  check: "M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z",
  card: "M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2H2Zm0 4h20v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2Z",
  register: "M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm2 4v3h12V7Zm0 6h4v4H6Z",
  reports: "M4 20h16v2H4Zm2-8h3v7H6Zm5-6h3v13h-3Zm5 3h3v10h-3Z",
  notes: "M5 3h11l4 4v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm2 8h10v2H7Zm0 4h7v2H7Z",
};

/** Route path → glyph. An unmapped route gets the neutral document icon. */
const TILE_ICON: Record<string, string> = {
  "/": "home",
  "/students": "students",
  "/classes": "school",
  "/attendance": "check",
  "/grades": "notes",
  "/payments": "card",
  "/cashier": "register",
  "/reports": "reports",
};

function Glyph({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
      <path d={ICONS[path] ?? ICONS.notes} />
    </svg>
  );
}

export function MobileNav({ items }: { items: AppRoute[] }) {
  const tiles = bottomTiles(items);
  if (!tiles.length) return null;

  return (
    <nav
      // `pb-[env(safe-area-inset-bottom)]` keeps the tiles above the iPhone
      // home indicator, which otherwise sits on top of the last row.
      className="fixed inset-x-0 bottom-0 z-30 grid border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
      style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}
      aria-label="Navigation principale"
    >
      {tiles.map((route) => (
        <NavLink
          key={route.path}
          to={route.path}
          // 56px tall: the legacy height, and comfortably above the 44px
          // minimum touch target with room for a label underneath.
          className={({ isActive }) =>
            `flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] ${
              isActive ? "font-semibold text-accent-600" : "text-text-muted"
            }`
          }
        >
          <Glyph path={TILE_ICON[route.path] ?? "notes"} />
          <span className="max-w-full truncate px-1">{route.nav!.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
